import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Check, X, ExternalLink } from "lucide-react";
import { API } from "@/lib/faro";
import DgtCriteria from "@/components/DgtCriteria";

function navBtnCls(active, answered) {
  if (active) return "bg-[var(--faro-blue)] text-white";
  if (answered) return "bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)]";
  return "bg-[var(--faro-bg)] text-[var(--faro-text-soft)] hover:bg-[rgba(30,42,143,0.1)]";
}

function scoreMessage(score) {
  if (score >= 9) return "¡Estás listo para el examen!";
  if (score >= 6) return "Vas por buen camino. Con clases dirigidas lo bordas.";
  return "Tranqui, esto se entrena. Con nuestro método aprobarás.";
}

export default function TheoryTest() {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [validated, setValidated] = useState(null); // {score,total,results}
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/theory-questions`).then((r) => setQuestions(r.data)).catch(() => {});
  }, []);

  if (!questions.length) {
    return (
      <section id="test" className="section-pad bg-[var(--faro-bg)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center text-[var(--faro-text-soft)]">
          Cargando test...
        </div>
      </section>
    );
  }

  const current = questions[idx];
  const total = questions.length;
  const progress = validated ? 100 : ((idx) / total) * 100;

  const select = (oIdx) => setAnswers((a) => ({ ...a, [current.id]: oIdx }));

  const next = () => {
    if (idx + 1 < total) setIdx(idx + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/theory-questions/validate`, { answers });
      setValidated(data);
      await axios.post(`${API}/test-results`, { score: data.score, total: data.total }).catch(() => {});
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setIdx(0);
    setAnswers({});
    setValidated(null);
  };

  const answeredAll = Object.keys(answers).length === total;

  return (
    <section id="test" className="section-pad bg-[var(--faro-bg)]" data-testid="theory-test-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="eyebrow">Test DGT de muestra</span>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3">
            Ponte a prueba.
          </h2>
          <p className="mt-3 text-lg text-[var(--faro-text-soft)]">
            {total} preguntas reales tipo DGT. Sin trampas, sin registro. Cuando termines, te decimos qué has fallado y por qué.
          </p>
          <a
            href="https://sedeclave.dgt.gob.es/WEB_NOTP_CONSULTA/consultaNota.faces"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dgt-notas-btn"
            className="mt-6 inline-flex items-center gap-2 bg-[var(--faro-blue)] hover:bg-[var(--faro-blue-deep)] text-white font-black uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors shadow-md"
          >
            Consultar nota del examen DGT
            <ExternalLink size={14} />
          </a>
          <p className="mt-2 text-xs text-[var(--faro-text-soft)]">
            Sede electrónica oficial de la DGT · Se abre en una pestaña nueva
          </p>
        </div>

        <DgtCriteria />

        <div className="mt-10 rounded-3xl border border-[rgba(30,42,143,0.12)] bg-white p-6 sm:p-10 shadow-xl shadow-[rgba(30,42,143,0.06)]">
          {!validated ? (
            <>
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-red)]">
                <span>Pregunta {idx + 1} de {total}</span>{" "}
                <span className="text-[var(--faro-text-soft)] normal-case tracking-normal font-semibold">
                  {Object.keys(answers).length}/{total} respondidas
                </span>
              </div>
              <div className="h-1.5 w-full bg-[rgba(30,42,143,0.08)] rounded-full overflow-hidden mt-3">
                <motion.div className="h-full bg-[var(--faro-red)]" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.25 }}
                  className="mt-6"
                >
                  <h3 className="font-display font-bold text-2xl sm:text-3xl text-[var(--faro-text)] leading-snug">
                    {current.question}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                    {current.options.map((op, i) => (
                      <button
                        key={`${current.id}-${i}`}
                        data-testid={`test-option-${current.id}-${i}`}
                        className="quiz-option"
                        data-selected={answers[current.id] === i}
                        onClick={() => select(i)}
                      >
                        <span className="font-display font-black text-[var(--faro-blue)] mr-3">{String.fromCharCode(65 + i)}.</span>
                        {op}
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-2 flex-wrap">
                  {questions.map((q, i) => (
                    <button
                      key={q.id}
                      data-testid={`test-nav-${i}`}
                      onClick={() => setIdx(i)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${navBtnCls(i === idx, answers[q.id] !== undefined)}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                {idx + 1 < total ? (
                  <button
                    data-testid="test-next"
                    onClick={next}
                    disabled={answers[current.id] === undefined}
                    className="btn-pill btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Siguiente <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    data-testid="test-submit"
                    onClick={submit}
                    disabled={!answeredAll || submitting}
                    className="btn-pill btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Corrigiendo..." : "Ver resultado"} <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              data-testid="test-results"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <span className="eyebrow">Resultado</span>
                  <h3 className="font-display font-black text-4xl sm:text-5xl mt-2 text-[var(--faro-text)]">
                    {validated.score} / {validated.total}
                  </h3>
                  <p className="mt-2 text-[var(--faro-text-soft)]">{scoreMessage(validated.score)}</p>
                </div>
                <button data-testid="test-restart" onClick={reset} className="btn-pill btn-ghost">
                  <RotateCcw size={16} /> Hacer de nuevo
                </button>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-4">
                {validated.results.map((r, i) => {
                  const q = questions.find((qq) => qq.id === r.id);
                  return (
                    <div
                      key={r.id}
                      data-testid={`test-result-${r.id}`}
                      className={`rounded-2xl p-4 sm:p-5 border ${
                        r.is_correct
                          ? "border-green-200 bg-green-50"
                          : "border-red-200 bg-red-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            r.is_correct ? "bg-green-500 text-white" : "bg-[var(--faro-red)] text-white"
                          }`}
                        >
                          {r.is_correct ? <Check size={16} /> : <X size={16} />}
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-[var(--faro-text)]">{i + 1}. {q.question}</div>
                          <div className="text-sm mt-1 text-[var(--faro-text-soft)]">
                            Respuesta correcta: <strong className="text-[var(--faro-blue)]">{q.options[r.correct_index]}</strong>
                          </div>
                          <div className="text-sm mt-2 text-[var(--faro-text)]">{r.explanation}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 rounded-2xl bg-[var(--faro-blue)] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h4 className="font-display font-bold text-xl">¿Listo para sacarte el carnet de verdad?</h4>
                  <p className="text-white/80 text-sm mt-1">Con nuestros profesores y método, el aprobado está a un clic.</p>
                </div>
                <a href="#contacto" data-testid="test-cta-enroll" className="btn-pill bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] hover:brightness-95">
                  Matricúlate <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
